/**
 * jsonwebtoken browser mock: replaces the Node-only `jsonwebtoken` package
 * with a pure-JS implementation using HMAC-SHA256 via the Web Crypto API
 * (available in all modern browsers and Node ≥ 15).
 *
 * sign() and verify() are async — callers inside AuthService already await them.
 *
 * Applied via resolve.alias in vite.config.ts and vitest.config.ts.
 */

function b64url(data: string): string {
  return btoa(data).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function b64urlEncode(obj: unknown): string {
  return b64url(JSON.stringify(obj));
}

function b64urlDecode(str: string): unknown {
  const pad = str.length % 4;
  const base64 = str.replace(/-/g, '+').replace(/_/g, '/') + (pad ? '='.repeat(4 - pad) : '');
  return JSON.parse(atob(base64));
}

async function importKey(secret: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign', 'verify']
  );
}

function toHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

function fromHex(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

export async function sign(
  payload: Record<string, unknown>,
  secret: string,
  options?: { expiresIn?: number }
): Promise<string> {
  const header = { alg: 'HS256', typ: 'JWT' };
  const now = Math.floor(Date.now() / 1000);
  const claims: Record<string, unknown> = { iat: now, ...payload };
  if (options?.expiresIn) claims.exp = now + options.expiresIn;

  const headerB64 = b64urlEncode(header);
  const claimsB64 = b64urlEncode(claims);
  const signingInput = `${headerB64}.${claimsB64}`;

  const key = await importKey(secret);
  const sigBuf = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(signingInput));
  const sig = b64url(toHex(sigBuf));

  return `${signingInput}.${sig}`;
}

export async function verify(token: string, secret: string): Promise<Record<string, unknown>> {
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw Object.assign(new Error('jwt malformed'), { name: 'JsonWebTokenError' });
  }

  const signingInput = `${parts[0]}.${parts[1]}`;
  const sigHex = atob(
    parts[2].replace(/-/g, '+').replace(/_/g, '/') + '=='.slice(0, (4 - parts[2].length % 4) % 4)
  );

  const key = await importKey(secret);
  const valid = await crypto.subtle.verify(
    'HMAC',
    key,
    fromHex(sigHex),
    new TextEncoder().encode(signingInput)
  );

  if (!valid) {
    throw Object.assign(new Error('invalid signature'), { name: 'JsonWebTokenError' });
  }

  const payload = b64urlDecode(parts[1]) as Record<string, unknown>;
  const now = Math.floor(Date.now() / 1000);
  if (typeof payload.exp === 'number' && payload.exp < now) {
    throw Object.assign(new Error('jwt expired'), { name: 'TokenExpiredError' });
  }

  return payload;
}

export function decode(token: string): Record<string, unknown> | null {
  try {
    return b64urlDecode(token.split('.')[1]) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export default { sign, verify, decode };
